import { ReactNode, useId } from 'react'

import { SectionInfoIcon } from 'src/components/Feedback/SectionInfoIcon/SectionInfoIcon'
import { cn } from 'src/utils'

export interface DetailSectionProps {
  title: string
  /** Shown behind an info icon beside the title, not as a subheading. */
  description?: ReactNode
  /**
   * Right-aligned slot in the section header. This is where a per-section
   * "Edit" control belongs once the screens become editable.
   */
  actions?: ReactNode
  /** Number of columns the fields lay out in on a wide viewport. */
  columns?: 1 | 2 | 3 | 4
  className?: string
  children: ReactNode
}

// Written out rather than composed, because Tailwind only ships a class it can
// see as a whole string in the source. Three and four columns step through two
// each on a mid-size viewport so nothing is squeezed on a laptop half-screen.
const COLUMN_CLASS: Record<1 | 2 | 3 | 4, string> = {
  1: 'sm:grid-cols-1',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-2 lg:grid-cols-3',
  4: 'sm:grid-cols-2 lg:grid-cols-4',
}

/** A bordered group of {@link DetailField}s with a heading. */
const DetailSection = ({
  title,
  description,
  actions,
  columns = 2,
  className,
  children,
}: DetailSectionProps) => {
  const headingId = useId()

  return (
    <section
      // Named by its own heading, so the title is not announced twice.
      aria-labelledby={headingId}
      className={cn(
        'flex flex-col gap-3 rounded-lg border border-doubleNickel-gray-200 p-4',
        className
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <h2
            id={headingId}
            className="text-sm font-semibold text-doubleNickel-gray-900"
          >
            {title}
          </h2>
          {description && (
            <SectionInfoIcon
              tooltip={description}
              ariaLabel={`About ${title}`}
            />
          )}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
      <div className={cn('grid grid-cols-1 gap-4', COLUMN_CLASS[columns])}>
        {children}
      </div>
    </section>
  )
}

export default DetailSection
